import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
  timelineRailAxisY,
} from "@/lib/timeline/marker/MarkerMetrics/metrics";
import { povCharacterAnchor } from "@/lib/timeline/marker/PovIndicator/anchor";
import { markerRailPose } from "@/lib/timeline/rail/RailPose/pose";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";
import type { ChapterTickPose } from "./marks";

const CLEARANCE_GAP_PX = 8;
const POV_BADGE_HEIGHT_PX = 40;
const POV_BADGE_HALF_W_PX = 24;

export interface TickObstacle {
  minX: number;
  maxX: number;
  topY: number;
}

/** Discs and the POV badge at a chapter start, for a fixed label height. */
export function chapterTickObstacles(
  layout: BookTimelineLayout,
  atMs: number,
  viewportWidth: number,
): TickObstacle[] {
  const motion = computeSceneMotion(
    atMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  const railY = timelineRailAxisY();
  const half = MARKER_DIAMETER / 2;
  const axisOffset = markerDiscAxisOffsetY();
  const obstacles: TickObstacle[] = [];
  for (const marker of layout.markers) {
    const pose = markerRailPose(marker.characterId, atMs, layout.rail);
    if (pose === null || pose.parked === true) {
      continue;
    }
    const radius = half * pose.scale;
    const cx = motion.groupCenterX + pose.dx;
    const cy = railY + pose.railDy + pose.dy;
    obstacles.push({
      minX: cx - radius,
      maxX: cx + radius,
      topY: cy - radius - axisOffset,
    });
  }
  const pov = povCharacterAnchor(layout, atMs, viewportWidth);
  if (pov !== null) {
    obstacles.push({
      minX: pov.anchorX - POV_BADGE_HALF_W_PX,
      maxX: pov.anchorX + POV_BADGE_HALF_W_PX,
      topY: pov.topY - POV_BADGE_HEIGHT_PX,
    });
  }
  return obstacles;
}

/** Always above the whole cast at chapter start, even if the label would fit between them. */
export function liftChapterTickPose(
  pose: ChapterTickPose,
  obstacles: readonly TickObstacle[],
): ChapterTickPose {
  let y = pose.y;
  for (const item of obstacles) {
    y = Math.min(y, item.topY - CLEARANCE_GAP_PX);
  }
  return { ...pose, y };
}
